import React, { createContext, useContext, useEffect, useReducer, useState } from "react";

import { useAppStore } from "../hooks/useAppStore";
import { initialState, actionAppReducer } from "../hooks/useAppReducer";
import { ContextType } from "../types/state";
import userSettingsService from "../service/userSettings.service";
import { IUserSettings } from "../types/userSettings";

const AppContext = createContext<ContextType>({ state: initialState });
export const useAppContext = () => useContext(AppContext);

type UserSettingsContextType = {
  userSettings: IUserSettings | null;
  setUserSettings: (settings: IUserSettings | null) => void;
};

const UserSettingsContext = createContext<UserSettingsContextType>({
  userSettings: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUserSettings: () => {},
});

export const useUserSettings = () => useContext(UserSettingsContext);

export const AppWrapper = ({ children }: { children: React.ReactNode }) => {
  const contextValue = useAppStore(useReducer(actionAppReducer, initialState));
  const [userSettings, setUserSettings] = useState<IUserSettings | null>(null);

  const loggedInUserId = contextValue.state.loggedInUserInfo?._id;

  useEffect(() => {
    if (!contextValue.isHydrated) return undefined;

    if (!loggedInUserId) {
      setUserSettings(null);
      return undefined;
    }

    let mounted = true;
    (async () => {
      const result = await userSettingsService.get();
      if (mounted) {
        setUserSettings(result);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [contextValue.isHydrated, loggedInUserId]);

  return (
    <AppContext.Provider value={contextValue}>
      <UserSettingsContext.Provider value={{ userSettings, setUserSettings }}>
        {children}
      </UserSettingsContext.Provider>
    </AppContext.Provider>
  );
};

